import { useEffect, useRef, useState } from 'react'
import { Navigate, useNavigate } from 'react-router'
import { ChevronLeft } from 'lucide-react'
import {
  Button,
  Input,
  Logo,
  MobileFrame,
  OrDivider,
  OtpInput,
  PhoneInput,
  SocialButton,
  formatPhone,
  DevCodeButton,
} from '@waago/ui'
import {
  ApiError,
  useAuth,
  devCodeAvailable,
  fetchDevCode,
  googleSignInAvailable,
  mountInvisibleGoogleButton,
} from '@waago/api'
import DemoLogins from '../../components/DemoLogins'

/**
 * AC-002 — Connexion.
 *
 * Implements Figma frames `4006:2644` and `4006:3738`: 32px gutters, the
 * wordmark at 176x58, a 28.43/700 heading, and 44px slate-100 fields.
 *
 * Phone is still the identity (spec §1.2), but it is no longer a code that
 * proves it on every sign-in. The operator was blunt about why: "for one
 * connection the system sends one OTP … it's very expensive". So the password
 * chosen at signup is the ordinary way in, and a code is spent only where
 * nothing else works — a password nobody remembers, and an account three wrong
 * guesses have locked.
 */

/** Spec: the code is valid for a window, and the resend is rate-limited. */
const RESEND_SECONDS = 25

/** There is no OAuth provider on the backend yet; say so rather than hang. */
const SOCIAL_PENDING = 'La connexion via un réseau social arrive bientôt. Utilisez votre numéro.'

/** The same floor the API enforces; checked here so the button says why. */
const MIN_PASSWORD = 8

/**
 * Which screen is showing.
 *
 * `code` is the unlock screen — a code and nothing else. `reset` asks for a
 * code *and* a new password, and serves both "I forgot mine" and "this account
 * never had one": same proof, same screen, and the person comes out of it with
 * a password rather than back where they started.
 */
type View = 'credentials' | 'code' | 'reset'

export default function LoginPage() {
  const navigate = useNavigate()
  const {
    state,
    loginWithPassword,
    requestUnlockCode,
    unlockWithCode,
    requestPasswordResetCode,
    resetPasswordWithCode,
    loginWithGoogle,
  } = useAuth()
  const [view, setView] = useState<View>('credentials')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [cooldown, setCooldown] = useState(0)
  const googleOverlayRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (cooldown <= 0) return
    const id = setTimeout(() => setCooldown((s) => s - 1), 1000)
    return () => clearTimeout(id)
  }, [cooldown])

  useEffect(() => {
    if (view !== 'credentials' || !googleSignInAvailable() || !googleOverlayRef.current) return
    return mountInvisibleGoogleButton(googleOverlayRef.current, (idToken) => {
      void (async () => {
        setError(null)
        setBusy(true)
        try {
          const result = await loginWithGoogle(idToken)
          if (!result.accountFound) {
            void navigate('/inscription/type', {
              state: {
                idToken,
                email: result.email,
                firstName: result.firstName,
                lastName: result.lastName,
                pictureUrl: result.pictureUrl,
              },
            })
          }
        } catch {
          setError('La connexion Google a échoué. Réessayez.')
        } finally {
          setBusy(false)
        }
      })()
    })
  }, [view, loginWithGoogle, navigate])

  if (state.status === 'authenticated') return <Navigate to="/" replace />

  function tooManyCodes(err: unknown): boolean {
    return err instanceof ApiError && err.status === 429
  }

  /** The ordinary path: no SMS is sent unless something below goes wrong. */
  async function signIn() {
    setError(null)
    setNotice(null)
    setBusy(true)
    try {
      await loginWithPassword(phone, password)
    } catch (err) {
      if (err instanceof ApiError && err.is('password-not-set')) {
        // An account from before passwords existed — or a number with no
        // account at all, which answers identically on purpose. Sending them
        // to the reset screen rather than to a plain code login is the
        // difference between getting in once and having a password afterwards.
        await startReset(
          'Aucun mot de passe n’est défini pour ce numéro. Entrez le code reçu par SMS et' +
            ' choisissez-en un.',
        )
      } else if (err instanceof ApiError && err.is('account-locked')) {
        await sendUnlockCode()
      } else {
        setError(
          tooManyCodes(err)
            ? 'Trop de tentatives. Réessayez dans quelques minutes.'
            : 'Numéro ou mot de passe incorrect.',
        )
      }
    } finally {
      setBusy(false)
    }
  }

  async function sendUnlockCode() {
    setError(null)
    try {
      await requestUnlockCode(phone)
      setNotice(
        'Trop de tentatives : ce compte a été bloqué. Un code vient de vous être envoyé pour le' +
          ' débloquer.',
      )
      setCode('')
      setView('code')
      setCooldown(RESEND_SECONDS)
    } catch (err) {
      setError(
        tooManyCodes(err)
          ? 'Trop de tentatives. Réessayez dans quelques minutes.'
          : "Impossible d'envoyer le code. Vérifiez le numéro.",
      )
    }
  }

  async function verify(value: string) {
    setError(null)
    setBusy(true)
    try {
      await unlockWithCode(phone, value)
    } catch {
      setError('Code incorrect ou expiré.')
    } finally {
      setBusy(false)
    }
  }

  /**
   * Sends the code for the reset screen.
   *
   * `why` is shown above it, because the person may not have asked for this —
   * they typed a password and were sent here.
   */
  async function startReset(why?: string) {
    setError(null)
    setNotice(why ?? null)
    if (phone.length < 12) {
      setError('Entrez d’abord votre numéro de téléphone.')
      return
    }
    setBusy(true)
    try {
      await requestPasswordResetCode(phone)
      setCode('')
      setNewPassword('')
      setView('reset')
      setCooldown(RESEND_SECONDS)
    } catch (err) {
      setError(
        tooManyCodes(err)
          ? 'Trop de tentatives. Réessayez dans quelques minutes.'
          : "Impossible d'envoyer le code. Vérifiez le numéro.",
      )
    } finally {
      setBusy(false)
    }
  }

  async function confirmReset() {
    setError(null)
    setBusy(true)
    try {
      await resetPasswordWithCode(phone, code, newPassword)
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 400 ? err.message : 'Code incorrect ou expiré.',
      )
    } finally {
      setBusy(false)
    }
  }

  const alert = error ? (
    <p
      role="alert"
      className="animate-slide-up rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700"
    >
      {error}
    </p>
  ) : null

  const info =
    notice && !error ? (
      <p className="animate-slide-up rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
        {notice}
      </p>
    ) : null

  /** Shared by the two screens that ask for a code, and by the reset screen. */
  const sentTo = (
    <p className="mt-3 text-sm leading-[22px] text-slate-500">
      Veuillez entrer le code de confirmation
      <br />
      envoyé au numéro
      <br />
      <span className="text-slate-900">{formatPhone(phone)}</span>
    </p>
  )

  const backToCredentials = (
    <button
      type="button"
      onClick={() => {
        setView('credentials')
        setCode('')
        setError(null)
        setNotice(null)
      }}
      className="text-danger underline"
    >
      Revenir à la connexion
    </button>
  )

  return (
    <MobileFrame>
      <main id="main" className="flex flex-1 flex-col px-8 pt-8 pb-10">
        {view === 'credentials' ? (
          <>
            <button
              type="button"
              onClick={() => void navigate('/bienvenue')}
              className="-ml-1 flex items-center gap-1.5 self-start text-sm text-slate-500"
            >
              <ChevronLeft className="size-5" aria-hidden="true" />
              Retour
            </button>

            <Logo className="mt-4" />

            <h1 className="text-brand-600 mt-9 text-[28px] leading-[34px] font-bold">Connexion</h1>
            <p className="mt-3 text-sm leading-[22px] text-slate-500">
              Entrer votre numéro de téléphone et votre mot de passe pour vous connecter.
            </p>

            <form
              className="mt-10 flex flex-col gap-4"
              onSubmit={(e) => {
                e.preventDefault()
                void signIn()
              }}
            >
              {alert}
              {info}
              <PhoneInput
                label="Numéro de téléphone"
                value={phone}
                onValueChange={setPhone}
                required
              />
              <Input
                label="Mot de passe"
                type="password"
                placeholder="Votre mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                onClick={() => void startReset()}
                disabled={busy}
                className="text-brand-600 self-end text-sm underline"
              >
                Mot de passe oublié&nbsp;?
              </button>
              <Button
                block
                size="lg"
                type="submit"
                disabled={phone.length < 12 || password.length < MIN_PASSWORD || busy}
              >
                {busy ? 'Connexion…' : 'Se connecter'}
              </Button>
            </form>

            <OrDivider className="mt-9" />

            <div className="mt-9 flex flex-col gap-4">
              {(['facebook', 'google'] as const).map((provider) =>
                provider === 'google' && googleSignInAvailable() ? (
                  <div key={provider} className="relative">
                    <SocialButton provider={provider} action="Se connecter avec" disabled={busy} />
                    {/* Google's real button, invisible and stretched over the one
                        above — see mountInvisibleGoogleButton for why. */}
                    <div
                      ref={googleOverlayRef}
                      className="absolute inset-0 overflow-hidden opacity-0"
                    />
                  </div>
                ) : (
                  <SocialButton
                    key={provider}
                    provider={provider}
                    action="Se connecter avec"
                    onClick={() => setError(SOCIAL_PENDING)}
                  />
                ),
              )}
            </div>

            <DemoLogins className="mt-8" />
          </>
        ) : null}

        {view === 'code' ? (
          <>
            <h1 className="mt-3 text-[28px] leading-[34px] font-bold text-slate-700">
              Débloquer le compte
            </h1>
            {sentTo}

            <div className="mt-9 flex flex-col gap-5">
              {alert}
              {info}
              <OtpInput
                value={code}
                onChange={setCode}
                disabled={busy}
                onComplete={(v) => void verify(v)}
              />
              {/* Development only: the code is hashed at rest, so without
                  this nobody can get past this screen without the phone it
                  went to. */}
              <DevCodeButton
                available={devCodeAvailable()}
                destination={phone}
                fetchCode={fetchDevCode}
                onCode={(value) => {
                  setCode(value)
                  void verify(value)
                }}
              />
              <p className="text-center text-sm text-slate-500">
                Aucun code reçu&nbsp;?{' '}
                {cooldown > 0 ? (
                  <span className="text-brand-600">Renvoyer dans {cooldown}s</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => void sendUnlockCode()}
                    disabled={busy}
                    className="text-brand-600 underline"
                  >
                    Renvoyer
                  </button>
                )}
              </p>
            </div>

            <div className="mt-9 flex flex-col gap-4">
              <Button
                block
                size="lg"
                disabled={code.length < 6 || busy}
                onClick={() => void verify(code)}
              >
                {busy ? 'Vérification…' : 'Vérifier'}
              </Button>
              <p className="text-center text-sm text-slate-500">{backToCredentials}</p>
            </div>
          </>
        ) : null}

        {view === 'reset' ? (
          <>
            <h1 className="mt-3 text-[28px] leading-[34px] font-bold text-slate-700">
              Nouveau mot de passe
            </h1>
            {sentTo}

            <form
              className="mt-9 flex flex-col gap-5"
              onSubmit={(e) => {
                e.preventDefault()
                void confirmReset()
              }}
            >
              {alert}
              {info}
              <OtpInput value={code} onChange={setCode} disabled={busy} />
              <DevCodeButton
                available={devCodeAvailable()}
                destination={phone}
                fetchCode={fetchDevCode}
                onCode={setCode}
              />
              <Input
                label="Nouveau mot de passe"
                type="password"
                placeholder={`Au moins ${MIN_PASSWORD} caractères`}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
              <p className="text-center text-sm text-slate-500">
                Aucun code reçu&nbsp;?{' '}
                {cooldown > 0 ? (
                  <span className="text-brand-600">Renvoyer dans {cooldown}s</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => void startReset()}
                    disabled={busy}
                    className="text-brand-600 underline"
                  >
                    Renvoyer
                  </button>
                )}
              </p>
              <Button
                block
                size="lg"
                type="submit"
                disabled={code.length < 6 || newPassword.length < MIN_PASSWORD || busy}
              >
                {busy ? 'Enregistrement…' : 'Enregistrer et se connecter'}
              </Button>
              <p className="text-center text-sm text-slate-500">{backToCredentials}</p>
            </form>
          </>
        ) : null}
      </main>
    </MobileFrame>
  )
}
